import React from 'react';
import { ArrowRight, TrendingDown, Clock, Building2 } from 'lucide-react';
import useScrollReveal from '../hooks/useScrollReveal';

// ---------------------------------------------------------------------------
// EDIT HERE: Tauschen Sie die Inhalte gegen Ihre echten anonymisierten Cases.
// Struktur pro Case:
//   industry: kurze Branchenbezeichnung (anonym)
//   volume:   Sendungsvolumen pro Jahr
//   duration: Projektlaufzeit
//   result:   Hauptergebnis (z. B. "-22 % Portokosten")
//   levers:   Welche der 5 Hebel wurden eingesetzt (Hebel 1-5)
//   situation:Ausgangslage in 1-2 Sätzen
//   approach: Vorgehen in 1-2 Sätzen
//   outcome:  Detailliertes Ergebnis in 1-2 Sätzen
// ---------------------------------------------------------------------------
const CASE_STUDIES = [
  {
    industry: 'Städtisches Klinikum',
    volume: '~ 400.000 Sendungen / Jahr',
    duration: '2 Monate',
    result: '-30 % Portokosten',
    levers: [1, 3],
    situation:
      'Eingleisige Konsolidierungsstruktur, historisch gewachsen und ohne ' +
      'wettbewerbliche Prüfung; Konditionen zuletzt 2020 nachverhandelt. ' +
      'Bestehender Werkverkehr zwischen den Standorten war nicht in die ' +
      'zentrale Versandsteuerung eingebunden.',
    approach:
      'Strukturierte Marktanalyse, Prüfung alternativer Einlieferungswege, ' +
      'gestaffelter Vertragswechsel mit definierten ' +
      'Service-Level-KPIs und sechsmonatiger operativer Übergangsphase.',
    outcome:
      'Senkung der jährlichen Portokosten um 30 %. Standortindividuelle ' +
      'Abholfenster realisiert, verbesserte Zustellqualität gegenüber dem ' +
      'Vorzustand. Jährliche Konditionsprüfung als verbindlicher ' +
      'Steuerungsprozess verankert.',
  },
  {
    industry: 'Stadtverwaltung',
    volume: '~ 750.000 Sendungen / Jahr',
    duration: '4 Monate',
    result: '-28 % Portokosten',
    levers: [2, 5],
    situation:
      'Rund 35 % der Sendungen überschritten die Gewichtsschwelle des ' +
      'Standardbriefs nur knapp und wurden konsequent als Kompaktbrief ' +
      'abgerechnet. Druckprozesse waren auf einseitigen Druck ausgelegt; ' +
      'eine systematische Klassen- und Volumensteuerung der Poststelle war ' +
      'nicht etabliert.',
    approach:
      'Klassen-Verteilungsanalyse über das gesamte Sendungsvolumen, ' +
      'Umstellung der Web2Print-Lösung auf Duplexdruck zur Reduktion des ' +
      'Seitenumfangs, Anpassung von Druckschemata und Umschlagformaten. ' +
      'Einführung eines monatlichen KPI-Reportings für die Poststelle.',
    outcome:
      'Senkung der jährlichen Portokosten um 28 % bei stabiler ' +
      'Zustellqualität. Verschiebung des Sendungsmix vom Kompakt- in den ' +
      'günstigeren Standardbrief. Transparentes monatliches Reporting; ' +
      'Poststellen-Prozesse erstmals dokumentiert und Fehlfrankierungen ' +
      'systematisch vermieden.',
  },
  {
    industry: 'MVZ-Laborgruppe · 3 Standorte',
    volume: '~ 120.000 – 190.000 Sendungen / Jahr',
    duration: '3 Monate',
    result: '-34 % Portokosten',
    levers: [1, 3, 5],
    situation:
      'Historisch gewachsene Poststellenstruktur an drei Laborstandorten ' +
      'ohne zentrale Steuerung. Jeder Standort frankierte eigenständig mit ' +
      'eigener Frankiermaschine — Wartungsverträge, Nachfüllzyklen und ' +
      'Fehlfrankierungen liefen unkontrolliert nebeneinander. Eine ' +
      'standortübergreifende Kostentransparenz fehlte vollständig; ' +
      'qualifiziertes Personal war dauerhaft mit manuellen Frankier- und ' +
      'Sortieraufgaben gebunden.',
    approach:
      'Strukturierte Ist-Analyse der Sendungsmengen, Produktklassen und ' +
      'Frankierprozesse an allen drei Standorten. Entwicklung eines ' +
      'einheitlichen Konsolidierungsmodells mit zentraler Einlieferung und ' +
      'vollständiger Ablösung der Frankiermaschinen durch unfrankierte ' +
      'Übergabe an den Konsolidierer. Einführung einer zentralen monatlichen ' +
      'Abrechnung mit standortindividuellem Reporting; begleitete ' +
      'Übergangsphase mit definiertem Onboarding-Plan je Standort.',
    outcome:
      'Senkung der jährlichen Portokosten um 34 %. Fehlfrankierungen ' +
      'vollständig beseitigt durch Ablösung aller Frankiermaschinen. ' +
      'Wertvolles Fachpersonal von manuellen Frankier- und Sortieraufgaben ' +
      'entlastet und für andere Verwaltungsaufgaben verfügbar gemacht. ' +
      'Einheitlicher Postprozess an allen drei Standorten etabliert; ' +
      'transparentes monatliches Reporting als fester Steuerungsprozess ' +
      'verankert.',
  },
];

const HEBEL_LABELS = {
  1: 'Konsolidierung',
  2: 'Portoklassen',
  3: 'Dienstleister-Steuerung',
  4: 'Datenqualität',
  5: 'Prozesse',
};

const CaseStudyCard = ({ caseStudy, index }) => {
  return (
    <div
      className="group bg-white rounded-2xl border border-slate-200 p-8 lg:p-10 shadow-sm hover:shadow-xl hover:border-slate-300 transition-all duration-300 flex flex-col h-full"
      data-testid={`case-study-card-${index + 1}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-slate-500 mb-2">
            <Building2 size={14} style={{ color: '#FFA266' }} />
            Referenzprojekt {String(index + 1).padStart(2, '0')}
          </div>
          <h3 className="text-xl lg:text-2xl font-bold text-slate-900 leading-tight">
            {caseStudy.industry}
          </h3>
        </div>
        <div
          className="flex-shrink-0 px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap"
          style={{
            backgroundColor: 'rgba(255, 162, 102, 0.12)',
            color: '#F08C4A',
          }}
        >
          {caseStudy.result}
        </div>
      </div>

      {/* Meta info */}
      <div className="grid grid-cols-2 gap-4 mb-6 pb-6 border-b border-slate-100">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
            Sendungsvolumen
          </div>
          <div className="text-sm text-slate-800 font-medium">
            {caseStudy.volume}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
            Projektlaufzeit
          </div>
          <div className="text-sm text-slate-800 font-medium flex items-center gap-1.5">
            <Clock size={13} className="text-slate-400" />
            {caseStudy.duration}
          </div>
        </div>
      </div>

      {/* Levers used */}
      <div className="mb-6">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
          Eingesetzte Hebel
        </div>
        <div className="flex flex-wrap gap-2">
          {caseStudy.levers.map((lever) => (
            <span
              key={lever}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-700"
            >
              <span className="font-bold text-slate-900">{lever}</span>
              <span className="text-slate-500">·</span>
              {HEBEL_LABELS[lever]}
            </span>
          ))}
        </div>
      </div>

      {/* Story */}
      <div className="space-y-5 flex-1">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold mb-1.5" style={{ color: '#F08C4A' }}>
            Ausgangslage
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            {caseStudy.situation}
          </p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-slate-700 mb-1.5">
            Vorgehen
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            {caseStudy.approach}
          </p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-slate-900 mb-1.5 flex items-center gap-1.5">
            <TrendingDown size={13} style={{ color: '#F08C4A' }} />
            Ergebnis
          </div>
          <p className="text-sm text-slate-800 leading-relaxed font-medium">
            {caseStudy.outcome}
          </p>
        </div>
      </div>
    </div>
  );
};

const CaseStudies = () => {
  const [ref, revealed] = useScrollReveal(0.1);

  return (
    <section
      id="referenzen"
      className="py-20 lg:py-32 bg-slate-50"
      data-testid="case-studies-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div
          ref={ref}
          className={`reveal-on-scroll ${revealed ? 'revealed' : ''}`}
        >
          {/* Header */}
          <div className="max-w-3xl mb-16">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase mb-6"
              style={{
                backgroundColor: 'rgba(255, 162, 102, 0.15)',
                color: '#F08C4A',
              }}
            >
              <ArrowRight size={14} />
              Referenzprojekte
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              Wie sich die Hebel in der{' '}
              <span style={{ color: '#FFA266' }}>Praxis bewähren</span>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Drei anonymisierte Beispiele aus laufenden und abgeschlossenen
              Mandaten. Detaillierte Projektdokumentation auf Anfrage und nach
              ausdrücklicher Freigabe der jeweiligen Klienten.
            </p>
          </div>

          {/* Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {CASE_STUDIES.map((cs, idx) => (
              <CaseStudyCard key={idx} caseStudy={cs} index={idx} />
            ))}
          </div>

          {/* Footnote */}
          <p className="text-xs text-slate-500 mt-10 max-w-3xl leading-relaxed">
            Alle Angaben zu Branche, Volumen und Ergebnissen wurden zur Wahrung
            der Klientenverhältnisse anonymisiert. Genannte Ergebnisse sind
            vertraglich vereinbarte und nachgewiesene Einsparungen, keine
            Hochrechnungen.
          </p>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
